import React, { useEffect, useRef, useState } from "react";
import HrnbLink from "./HrnbLink";
import HrnbButton from "./HrnbButton";
import { LogoutIcon } from "../../icons";
import "../../css/components/controls/HrnbNavbarPopup.css";

function getFocusable(container) {
    if (!container) return [];
    return Array.from(
        container.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
}

export default function HrnbNavbarPopup({
                                            anchorRef,
                                            isOpen,
                                            onClose,
                                            items = [],
                                            user = null,
                                            onLogout,
                                            onLogin,
                                            align = "right",
                                            extra = null,
                                            dropdowns = [],
                                            expandedDropdown,
                                            onExpandDropdown,
                                        }) {
    const popupRef = useRef(null);
    const lastActiveRef = useRef(null);
    const [style, setStyle] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setReady(false);
        const raf = requestAnimationFrame(() => setReady(true));
        return () => cancelAnimationFrame(raf);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !ready) return;
        const anchor = anchorRef?.current;
        const popup = popupRef.current;
        if (!anchor || !popup) return;

        const anchorRect = anchor.getBoundingClientRect();
        const margin = 8;
        const padding = 8;

        // Popup привязан к правому краю,
        // но если он не помещается — ограничиваем ширину
        const distToRight = window.innerWidth - anchorRect.right;
        const popupWidth = popup.offsetWidth || 200;

        // Если места мало — привязываем к правому краю экрана
        // Если места достаточно — привязываем к правому краю кнопки
        let right;
        if (distToRight < popupWidth + padding) {
            right = padding; // Привязан к правому краю экрана
        } else {
            right = distToRight + padding; // Привязан к правому краю кнопки
        }

        const top = anchorRect.bottom + margin;

        setStyle({
            top: `${Math.round(top)}px`,
            right: `${Math.round(right)}px`,
        });
    }, [isOpen, ready, anchorRef]);

    useEffect(() => {
        if (!isOpen) return;
        const popup = popupRef.current;
        lastActiveRef.current = document.activeElement;
        requestAnimationFrame(() => {
            const f = getFocusable(popup);
            if (f.length) f[0].focus();
            else if (popup) popup.focus();
        });
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
            } else if (e.key === "Tab") {
                const focusable = getFocusable(popup);
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        const onDocClick = (e) => {
            const anchor = anchorRef?.current;
            if (popup && !popup.contains(e.target) && (!anchor || !anchor.contains(e.target))) {
                onClose?.();
            }
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDocClick);
        window.addEventListener("resize", onClose);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDocClick);
            window.removeEventListener("resize", onClose);
            if (lastActiveRef.current instanceof HTMLElement) lastActiveRef.current.focus();
        };
    }, [isOpen, anchorRef, onClose]);

    if (!isOpen || !ready) return null;

    return (
        <div
            ref={popupRef}
            className="hrnb-navbar-popup"
            style={style}
            role="menu"
            aria-orientation="vertical"
            tabIndex={-1}
        >
            <div className="hrnb-navbar-popup__arrow" aria-hidden="true"/>
            <div className="hrnb-navbar-popup__inner">
                {user && (
                    <div className="hrnb-navbar-popup__user">
                        <div className="hrnb-navbar-popup__user-avatar">
                            {user.displayName?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="hrnb-navbar-popup__user-info">
                            <div className="hrnb-navbar-popup__user-name">{user.displayName || user.username}</div>
                            {user.email && <div className="hrnb-navbar-popup__user-email">{user.email}</div>}
                        </div>
                    </div>
                )}

                <ul className="hrnb-navbar-popup__list" role="menu">
                    {items.map((it) => (
                        <li key={it.key || it.label} role="none">
                            {it.isDropdown ? (
                                <div>
                                    <HrnbButton
                                        type="button"
                                        className="hrnb-navbar-popup__item hrnb-navbar-popup__item--dropdown"
                                        role="menuitem"
                                        onClick={() => {
                                            onExpandDropdown?.(expandedDropdown === it.key ? null : it.key);
                                        }}
                                    >
                                        {it.icon && <span className="icon">{it.icon}</span>}
                                        <span className="label">{it.label}</span>
                                        <span className="hrnb-navbar-popup__arrow">
                                            {expandedDropdown === it.key ? '▲' : '▼'}
                                        </span>
                                    </HrnbButton>
                                    {expandedDropdown === it.key && (
                                        <ul className="hrnb-navbar-popup__submenu">
                                            {it.items.map((subItem) => (
                                                <li key={subItem.to}>
                                                    <HrnbLink
                                                        to={subItem.to}
                                                        className="hrnb-navbar-popup__item hrnb-navbar-popup__submenu-item"
                                                        onClick={() => onClose?.()}
                                                    >
                                                        {subItem.icon && <span className="icon">{subItem.icon}</span>}
                                                        <span className="label">{subItem.label}</span>
                                                    </HrnbLink>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ) : it.to ? (
                                <HrnbLink to={it.to} className="hrnb-navbar-popup__item" role="menuitem"
                                          onClick={() => onClose?.()}>
                                    {it.icon && <span className="icon">{it.icon}</span>}
                                    <span className="label">{it.label}</span>
                                </HrnbLink>
                            ) : (
                                <HrnbButton
                                    type="button"
                                    className="hrnb-navbar-popup__item"
                                    role="menuitem"
                                    onClick={() => {
                                        it.onClick?.();
                                        onClose?.();
                                    }}
                                >
                                    {it.icon && <span className="icon">{it.icon}</span>}
                                    <span className="label">{it.label}</span>
                                </HrnbButton>
                            )}
                        </li>
                    ))}
                </ul>
                {extra && <div className="hrnb-navbar-popup__extra">{extra}</div>}

                {user && (
                    <div className="hrnb-navbar-popup__footer">
                        <HrnbButton
                            type="button"
                            className="hrnb-navbar-popup__logout-btn"
                            onClick={() => {
                                onLogout?.();
                                onClose?.();
                            }}
                        >
                            <LogoutIcon className="hrnb-navbar-popup__logout-icon" />
                            <span>Выйти</span>
                        </HrnbButton>
                    </div>
                )}

                {!user && (
                    <div className="hrnb-navbar-popup__footer">
                        <HrnbButton
                            type="button"
                            className="hrnb-navbar-popup__login-btn"
                            onClick={() => {
                                onLogin?.();
                                onClose?.();
                            }}
                        >
                            Войти
                        </HrnbButton>
                    </div>
                )}
            </div>
        </div>
    );
}
